import React, { useEffect, useRef } from "react";
import { useLang } from "../lib/i18n";

const StoryVideo = ({ src, poster, label, testid }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.2 }
    );
    obs.observe(v);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden border border-[color:var(--goldenelia-gold)]/25 hover:border-[color:var(--goldenelia-gold)]/55 transition-colors duration-500"
      style={{ aspectRatio: "4 / 5" }}
    >
      <video
        ref={ref}
        className="absolute inset-0 w-full h-full object-cover"
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        autoPlay
        preload="auto"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.85) 100%)",
        }}
      />
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
        <p className="font-display text-[9px] tracking-[0.45em] text-[color:var(--goldenelia-gold)] uppercase mb-1">
          {label.eyebrow}
        </p>
        <p className="font-serif-luxury italic text-base sm:text-lg text-[color:var(--goldenelia-cream)] leading-tight">
          {label.title}
        </p>
      </div>
      {/* Gold corner accents */}
      <span className="pointer-events-none absolute top-2 left-2 w-5 h-px bg-[color:var(--goldenelia-gold)]/70" />
      <span className="pointer-events-none absolute top-2 left-2 w-px h-5 bg-[color:var(--goldenelia-gold)]/70" />
      <span className="pointer-events-none absolute bottom-2 right-2 w-5 h-px bg-[color:var(--goldenelia-gold)]/70" />
      <span className="pointer-events-none absolute bottom-2 right-2 w-px h-5 bg-[color:var(--goldenelia-gold)]/70" />
    </div>
  );
};

export const About = () => {
  const { t } = useLang();
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-32 sm:py-48 px-6 overflow-hidden"
    >
      {/* Black marble texture background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url('/marble-dark.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.4,
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.55) 18%, rgba(0,0,0,0.55) 82%, rgba(0,0,0,0.8) 100%)",
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-16 items-start">
          {/* Left: Story */}
          <div className="max-w-2xl">
            <p
              data-testid="about-eyebrow"
              className="font-display text-[10px] tracking-[0.55em] text-[color:var(--goldenelia-gold)] uppercase mb-6"
            >
              {t("about.eyebrow")}
            </p>
            <h2
              data-testid="about-title"
              className="font-serif-luxury text-4xl sm:text-5xl lg:text-6xl gold-gradient-text mb-8 leading-[1.05] whitespace-pre-line italic font-light"
            >
              {t("about.title")}
            </h2>
            <div className="gold-divider max-w-[180px] mb-8 mx-0">
              <span className="diamond" />
            </div>
            <p data-testid="about-p1" className="font-serif-luxury text-lg text-[color:var(--goldenelia-cream)]/85 leading-relaxed mb-6 italic">
              {t("about.p1")}
            </p>
            <p data-testid="about-p2" className="font-body text-[15px] text-[color:var(--goldenelia-cream)]/72 leading-[1.85] mb-5">
              {t("about.p2")}
            </p>
            <p data-testid="about-p3" className="font-body text-[15px] text-[color:var(--goldenelia-cream)]/72 leading-[1.85]">
              {t("about.p3")}
            </p>

            <div className="mt-10 pt-8 border-t border-[color:var(--goldenelia-gold)]/15">
              <p className="font-display text-[10px] tracking-[0.45em] text-[color:var(--goldenelia-gold)] uppercase mb-2">
                {t("about.signEyebrow")}
              </p>
              <p className="font-serif-luxury italic text-xl text-[color:var(--goldenelia-cream)]">
                {t("about.signature")}
              </p>
            </div>
          </div>

          {/* Right: 2 cinematic production videos */}
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 lg:w-[300px] xl:w-[340px]">
            <StoryVideo
              testid="story-video-oil"
              src="/story/oil_production.mp4"
              poster="/story/oil_production.jpg"
              label={{ eyebrow: t("about.videos.oilEyebrow"), title: t("about.videos.oilTitle") }}
            />
            <StoryVideo
              testid="story-video-wine"
              src="/story/wine_production.mp4"
              poster="/story/wine_production.jpg"
              label={{ eyebrow: t("about.videos.wineEyebrow"), title: t("about.videos.wineTitle") }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
